import React from "react";
import { Link } from "react-router-dom";
import { Facebook, MapPin, Phone, Mail, Clock } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { BUSINESS } from "@/i18n/content";

export const Footer = () => {
  const { tr, lang, setLang } = useLang();

  const socials = [
    { Icon: Facebook, label: "Facebook", key: "fb", url: "https://www.facebook.com/people/Alma-Y-Lengua-Bilingual-Learning-Center/100093597717101/" },
  ];

  const links = [
    { to: "/", label: tr.nav.home },
    { to: "/about", label: tr.nav.about },
    { to: "/services", label: tr.nav.services },
    { to: "/guide", label: tr.nav.guide },
    { to: "/exploradores", label: tr.nav.exploradores },
    { to: "/contact", label: tr.nav.contact },
  ];

  return (
    <footer className="bg-navy text-white grain" data-testid="site-footer">
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-20 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div className="lg:col-span-1">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-2xl bg-white text-navy grid place-items-center font-heading font-extrabold text-lg">
              AL
            </div>
            <span className="font-heading font-extrabold text-lg">Alma Y Lengua</span>
          </div>
          <p className="text-white/60 text-sm leading-relaxed max-w-xs">{tr.footer.desc}</p>
        </div>

        <div>
          <h4 className="text-sm uppercase tracking-[0.2em] text-lightblue font-bold mb-5">{tr.footer.nav}</h4>
          <ul className="space-y-3">
            {links.map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={l.to === "/exploradores" ? "footer-exploradores-link" : undefined} className="text-white/70 hover:text-gold transition-colors">{l.label}</Link>
              </li>
            ))}
            <li>
              <Link to="/admin" data-testid="footer-admin-link" className="text-white/30 hover:text-gold transition-colors text-sm">{tr.nav.admin}</Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-sm uppercase tracking-[0.2em] text-lightblue font-bold mb-5">{tr.common.address}</h4>
          <ul className="space-y-4 text-white/70 text-sm">
            <li className="flex gap-3"><MapPin size={18} className="text-gold shrink-0" /><span>{BUSINESS.addressLine1}<br />{BUSINESS.addressLine2}</span></li>
            <li className="flex gap-3"><Phone size={18} className="text-gold shrink-0" /><a href={BUSINESS.phoneHref} className="hover:text-gold transition-colors">{BUSINESS.phone}</a></li>
            <li className="flex gap-3"><Mail size={18} className="text-gold shrink-0" /><a href={BUSINESS.emailHref} className="hover:text-gold transition-colors break-all">{BUSINESS.email}</a></li>
            <li className="flex gap-3"><Clock size={18} className="text-gold shrink-0" /><span>{tr.common.byAppointment}</span></li>
          </ul>
        </div>

        <div>
          <h4 className="text-sm uppercase tracking-[0.2em] text-lightblue font-bold mb-5">{tr.footer.follow}</h4>
          <div className="flex gap-3 mb-6">
            {socials.map(({ Icon, label, key, url }) => (
              <a key={key} href={url} target="_blank" rel="noopener noreferrer" title={label} aria-label={label} data-testid={`social-${key}`}
                className="w-11 h-11 grid place-items-center rounded-xl bg-white/10 text-white/70 hover:bg-gold hover:text-navy transition-colors">
                <Icon size={18} />
              </a>
            ))}
          </div>
          <div className="mt-2">
            <div className="inline-flex items-center rounded-full bg-white/10 p-1 text-xs font-bold">
              <button onClick={() => setLang("en")} className={`px-3 py-1.5 rounded-full transition-colors ${lang === "en" ? "bg-white text-navy" : "text-white/70"}`}>English</button>
              <button onClick={() => setLang("es")} className={`px-3 py-1.5 rounded-full transition-colors ${lang === "es" ? "bg-white text-navy" : "text-white/70"}`}>Espa&#241;ol</button>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <p>{tr.footer.rights}</p>
          <div className="flex gap-6">
            <span className="hover:text-gold transition-colors cursor-pointer" data-testid="footer-privacy">{tr.footer.privacy}</span>
            <span className="hover:text-gold transition-colors cursor-pointer" data-testid="footer-accessibility">{tr.footer.accessibility}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
